import { useState, type FormEvent } from 'react';
import { useAgentPermission } from '@entities/user';
import {
  apiErrorText,
  useCreateCeoPaymentApprovalMutation,
  useDecideCeoPaymentApprovalMutation,
  useGetCeoPaymentApprovalsQuery,
} from '@shared/api';
import { parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { ApprovalsKpiGrid } from './ApprovalsKpiGrid';
import { ApprovalsTable } from './ApprovalsTable';
import { CreateApprovalForm } from './CreateApprovalForm';
import {
  ErrorAlert,
  ErrorRow,
  FinancierLink,
  HeaderActionsRight,
  HeaderRow,
  Section,
  SubTitleText,
  Title,
} from './styles';
import type { ApprovalDecision, InlineAmountsMap } from './types';

export function CeoApprovalsView() {
  const { can_write } = useAgentPermission('ceo');
  const { data, isLoading, isError, refetch } = useGetCeoPaymentApprovalsQuery();
  const [decide, { isLoading: isDeciding }] = useDecideCeoPaymentApprovalMutation();
  const [createApproval, { isLoading: isCreating }] = useCreateCeoPaymentApprovalMutation();

  const [showAdd, setShowAdd] = useState(false);
  const [counterparty, setCounterparty] = useState('');
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('');
  const [priority, setPriority] = useState('high');
  const [category, setCategory] = useState('materials');
  const [initiator, setInitiator] = useState('');
  const [budgetStatus, setBudgetStatus] = useState('within_limit');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [inlineAmounts, setInlineAmounts] = useState<InlineAmountsMap>({});

  const handleDecide = async (
    id: string,
    decision: ApprovalDecision,
    approvedAmount?: number,
    comment?: string
  ) => {
    try {
      setErrorMsg(null);
      await decide({
        id,
        decision,
        approved_amount: approvedAmount,
        carryover_remainder: true,
        decision_comment: comment,
      }).unwrap();
      // Решение принято: поле строки снова показывает сумму с сервера.
      setInlineAmounts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err, 'Не удалось вынести решение по заявке'));
    }
  };

  if (isLoading) {
    return <Card>Загрузка платёжной позиции и реестра акцепта…</Card>;
  }

  if (isError || !data) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить платёжную позицию и реестр акцепта.</span>
          <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!counterparty.trim()) return;
    const amt = parseMoney(amount);
    if (amt === null || amt <= 0) {
      setAmountError('Укажите сумму больше нуля, например 125 000,50');
      return;
    }
    setAmountError(null);
    try {
      setFormError(null);
      await createApproval({
        counterparty_name: counterparty.trim(),
        amount: amt,
        purpose: purpose.trim() || 'Оплата по счёту',
        priority,
        category,
        initiator_name: initiator.trim() || 'Финансовая служба',
        budget_status: budgetStatus,
      }).unwrap();
      setCounterparty('');
      setAmount('');
      setPurpose('');
      setShowAdd(false);
    } catch (err: unknown) {
      setFormError(apiErrorText(err, 'Не удалось добавить заявку на акцепт'));
    }
  }

  const handleInlineAmountChange = (id: string, text: string) => {
    setInlineAmounts((prev) => ({
      ...prev,
      [id]: text,
    }));
  };

  return (
    <>
      <Section>
        <HeaderRow>
          <div>
            <Title>Платёжная позиция дня и акцепт реестра платежей Генеральным директором</Title>
            <SubTitleText>
              Интерактивный расчёт остатка ликвидности после исполнения акцептованных заявок и
              контроль превышения лимитов статей БДДС
            </SubTitleText>
          </div>
          <HeaderActionsRight>
            <FinancierLink to="/agents/finance/requests">
              Управление фабрикой платежей в ОСНОВА.Финансист →
            </FinancierLink>
            {can_write && (
              <Button $size="sm" onClick={() => setShowAdd((v) => !v)}>
                + Внести заявку в реестр
              </Button>
            )}
          </HeaderActionsRight>
        </HeaderRow>

        {can_write && showAdd && (
          <CreateApprovalForm
            counterparty={counterparty}
            setCounterparty={setCounterparty}
            amount={amount}
            setAmount={(v) => {
              setAmount(v);
              setAmountError(null);
            }}
            amountError={amountError}
            priority={priority}
            setPriority={setPriority}
            category={category}
            setCategory={setCategory}
            budgetStatus={budgetStatus}
            setBudgetStatus={setBudgetStatus}
            initiator={initiator}
            setInitiator={setInitiator}
            purpose={purpose}
            setPurpose={setPurpose}
            isCreating={isCreating}
            errorMsg={formError}
            onSubmit={handleCreate}
            onClose={() => setShowAdd(false)}
          />
        )}

        <ApprovalsKpiGrid
          availableCashStart={data.available_cash_start}
          pendingApprovalsTotal={data.pending_approvals_total}
          pendingCriticalTotal={data.pending_critical_total}
          approvedTodayTotal={data.approved_today_total}
          deferredTodayTotal={data.deferred_today_total}
          projectedCashAfterApproved={data.projected_cash_after_approved}
          projectedCashAfterAllPending={data.projected_cash_after_all_pending}
        />
      </Section>

      <Section>
        {errorMsg && (
          <ErrorAlert>
            <span>{errorMsg}</span>
            <Button $size="sm" $variant="ghost" onClick={() => setErrorMsg(null)}>
              ✕
            </Button>
          </ErrorAlert>
        )}

        <ApprovalsTable
          items={data.items}
          inlineAmounts={inlineAmounts}
          onInlineAmountChange={handleInlineAmountChange}
          can_write={can_write}
          isDeciding={isDeciding}
          onDecide={handleDecide}
        />
      </Section>
    </>
  );
}
